/**
 * The information architecture — verbatim from the prototype's `NAV`.
 *
 * Nineteen sections, 117 screens, 58 of them Phase 1. The tuple form is the
 * design's own: `[section label, section tone, [[id, label, phase], …]]`.
 * Kept compact rather than expanded into objects so a diff against
 * `docs/design/reference/app-source.js` stays readable — this file is
 * transcribed design, and a change to it should be a decision someone made
 * rather than a line someone missed.
 *
 * Eighteen of the nineteen ARE that transcription. The nineteenth — Handles —
 * is not, because the prototype predates the design brief's §6.18 and has no
 * line to transcribe; the brief states as much of its own screen lists. So the
 * counts above no longer match `app-source.js` and are not meant to: they are
 * the transcription plus every section added by a design decision since, and
 * the tests below pin them so that the next addition is also a decision.
 *
 * Phase 1 here is the STAKEHOLDER scope decision of 2026-07-31: the prototype
 * governs, overriding the thin console described in go-wallet
 * `docs/SPEC-phase1.md` §1.
 */

import type { Tone } from './spec/types'

export type Phase = 1 | 2

/** `[id, label, phase]`. */
export type NavItem = readonly [id: string, label: string, phase: Phase]

/** `[label, tone, items]`. */
export type NavSection = readonly [label: string, tone: Tone, items: readonly NavItem[]]

export const NAV: readonly NavSection[] = [
  ['Dashboard', 'g', [['dashboard', 'Today', 1]]],
  ['Customers', 'n', [['cust-list', 'Customer list', 1], ['cust-360', 'Customer 360', 1], ['cust-biz', 'Businesses & members', 2], ['cust-seg', 'Segments', 2], ['cust-wait', 'Waitlist', 2]]],
  ['Onboarding', 'a', [['onb-queue', 'Application queue', 1], ['onb-sla', 'SLA breaches', 1], ['onb-evidence', 'Evidence review', 1], ['onb-reverify', 'Re-verification due', 2], ['onb-emi', 'EMI acceptance queue', 1], ['onb-content', 'Status-experience content', 2]]],
  ['Money', 'n', [['mny-tx', 'Transactions', 1], ['mny-cashouts', 'Cash-outs', 1], ['mny-internal', 'Internal transfers', 2], ['mny-events', 'Provider event feed', 1], ['mny-webhooks', 'Webhook deliveries', 1], ['mny-enrich', 'Enrichment queue', 1], ['mny-outbox', 'Notification outbox', 1]]],
  ['Ledger & Reconciliation', 'r', [['lg-coa', 'Chart of accounts', 1], ['lg-journal', 'Journal explorer', 1], ['lg-vs', 'Balances vs provider', 1], ['lg-runs', 'Reconciliation runs', 1], ['lg-disc', 'Discrepancy cases', 1], ['lg-suspense', 'Suspense clearance', 1], ['lg-liveness', 'Reconciler liveness', 1], ['lg-replay', 'Replay verification', 2]]],
  ['Recognition', 'n', [['rc-coverage', 'Coverage', 1], ['rc-catalog', 'Payer catalog', 1], ['rc-platforms', 'Platforms', 1], ['rc-orch', 'Orchestrators', 1], ['rc-cand', 'Candidate queue', 1], ['rc-feedback', 'Feedback stream', 1], ['rc-gaps', 'Recognition gaps', 2], ['rc-peruser', 'Per-user mappings', 1], ['rc-retag', 'Retag jobs', 2], ['rc-sim', 'Match simulator', 1]]],
  ['Risk & Compliance', 'r', [['rk-decisions', 'Decision explorer', 1], ['rk-review', 'Review queue', 1], ['rk-stepup', 'Step-up queue', 1], ['rk-typologies', 'Typologies', 2], ['rk-limits', 'Limits & counters', 1], ['rk-aml', 'AML escalations', 1], ['rk-screening', 'Screening hits', 1], ['rk-cases', 'Cases', 1], ['rk-entitlements', 'Entitlement matrix', 1], ['rk-inflow', 'Approved inflow sources', 1], ['rk-state', 'Account state controls', 1]]],
  /*
   * Handles — directly under Risk & Compliance, where the design brief's menu
   * tree puts it and where an operator will look for it (§6.18).
   *
   * NOT transcribed from `docs/design/reference/app-source.js`. That file
   * predates §6.18 — the brief says so itself — so there was no line to
   * transcribe, and this section is the first entry in NAV that is a decision
   * rather than a transcription. It is here rather than beside the decision
   * explorer because §6.18 is numbered after §6.17 to avoid renumbering §6.7 to
   * §6.17 and silently breaking §5.4's citation of §6.15: follow the tree, not
   * the numbering.
   *
   * Two items, and only two. The claim journal is NOT a left-rail destination:
   * the only route to it is per-customer, so it lives at Customer 360 → Handles
   * (§6.18.3), and a nav entry for it would promise a cross-customer list no
   * endpoint serves.
   */
  ['Handles', 'a', [['hd-reviews', 'Impersonation review queue', 1], ['hd-terms', 'Reserved terms', 1]]],
  ['Pricing & Billing', 'n', [['pr-lists', 'Price lists', 2], ['pr-items', 'Fee items', 2], ['pr-assign', 'Assignments', 2], ['pr-waivers', 'Waivers & promotions', 2], ['pr-runs', 'Billing runs', 2], ['pr-dunning', 'Dunning', 2], ['pr-events', 'Fee events', 2], ['pr-disclosure', 'Disclosure preview', 2]]],
  ['Referrals & Partners', 'n', [['rf-partners', 'Partners', 2], ['rf-codes', 'Codes & links', 2], ['rf-schemes', 'Schemes', 2], ['rf-accruals', 'Accruals', 2], ['rf-payouts', 'Payout approvals', 2], ['rf-clawbacks', 'Clawbacks', 2], ['rf-fraud', 'Fraud flags', 2], ['rf-invoices', 'Self-billing invoices', 2], ['rf-funnel', 'Partner funnel', 2]]],
  ['Providers & Routing', 'n', [['pv-registry', 'Provider registry', 1], ['pv-methods', 'Method catalog', 1], ['pv-offerings', 'Offerings & buy prices', 2], ['pv-routing', 'Routing rules & pins', 2], ['pv-health', 'Provider health', 1], ['pv-caps', 'Capability matrix', 1], ['pv-invoices', 'Provider invoice recon', 2]]],
  ['Storefront', 'n', [['sf-catalog', 'Brand catalog', 2], ['sf-orders', 'Orders', 2], ['sf-failed', 'Failed & refund queue', 1], ['sf-margin', 'Supplier costs & margin', 2], ['sf-vat', 'VAT/OSS rates', 2]]],
  ['Reporting', 'n', [['rp-ops', 'Ops metrics', 1], ['rp-funnel', 'Activation funnel', 2], ['rp-pl', 'P&L', 2], ['rp-profit', 'Customer profitability', 2], ['rp-cohorts', 'Cohorts (CAC/LTV)', 2], ['rp-deflection', 'Deflection metrics', 2], ['rp-tieout', 'Tie-out status', 1], ['rp-exports', 'Export centre', 2]]],
  ['Statements & Documents', 'n', [['st-runs', 'Statement runs', 2], ['st-versions', 'Versions & hashes', 2], ['st-regen', 'Regeneration', 2], ['st-replication', 'Object-storage replication', 2]]],
  ['Content & Localization', 'n', [['ct-help', 'Help centre', 2], ['ct-trees', 'Troubleshooting trees', 2], ['ct-payout', 'Payout guidance', 1], ['ct-templates', 'Notification templates', 2], ['ct-strings', 'String catalogs', 2], ['ct-backlog', 'Translation backlog', 2], ['ct-authoritative', 'Authoritative-language flags', 2]]],
  ['Configuration', 'n', [['cf-juris', 'Jurisdictions & gating', 1], ['cf-langs', 'Languages', 2], ['cf-gates', 'Feature & capability gates', 2], ['cf-sla', 'SLA targets', 1], ['cf-limits', 'Limit policies', 2], ['cf-segments', 'Segments', 2], ['cf-reasons', 'Reason-code registry', 1]]],
  ['Platform Ops', 'n', [['pl-cells', 'Cells & roles', 1], ['pl-workers', 'Worker health', 1], ['pl-outbox', 'Outbox monitor', 1], ['pl-jobs', 'Job runs', 1], ['pl-build', 'Build & version', 1]]],
  ['Access & Audit', 'n', [['ac-audit', 'Audit log', 1], ['ac-staff', 'Staff directory', 2], ['ac-break', 'Break-glass', 1], ['ac-reviews', 'Access reviews', 2], ['ac-gdpr', 'GDPR requests', 2]]],
  ['Support', 'n', [['sp-ticket', 'Ticket context', 2], ['sp-deflection', 'Deflection insights', 2]]]
]

/**
 * The rail's pinned shortcuts — `[id, label, tone]`.
 *
 * These are the five things a shift starts from. The pin is navigation and is
 * design; the tone is the colour its badge takes when there is a badge.
 *
 * # There is no count here, and that is deliberate (#30)
 *
 * This tuple used to carry one — `['lg-disc', 'Discrepancy cases', 7, 'r']` —
 * and the rail rendered it. Seven is design-corpus data: nobody read seven
 * discrepancy cases, and a pinned count is the first number an operator sees
 * at the start of a shift. Once one screen was wired to `/v1/admin` the rail
 * could show an invented 7 two inches from a header reading "Live · postgres",
 * with nothing to tell them apart.
 *
 * **Restore it from `GET /v1/admin/queues`, never from here.** That endpoint
 * does not exist yet (go-wallet, tracked by #30); when it does it answers per
 * queue id in the dashboard's `{availability, value?, reason?, provenance?}`
 * shape, so the rail can distinguish an empty queue from an unbuilt one from a
 * failed read. A count typed back into this file would be a count nobody read.
 */
export const PINS: readonly (readonly [id: string, label: string, tone: Tone])[] = [['lg-disc', 'Discrepancy cases', 'r'], ['rc-cand', 'Candidate queue', 'a'], ['rk-aml', 'AML escalations', 'a'], ['onb-sla', 'SLA breaches', 'a'], ['cust-360', 'Customer 360', 'n']]

/*
 * `COUNTS` — the per-screen queue badges — was here and is deleted (#30).
 *
 * Same reason as the pins above: sixteen fixture counts, "Review queue 11" and
 * "Candidate queue 34" among them, rendered into global chrome where the
 * per-screen provenance chip cannot answer for them. The rail now renders no
 * counts at all, which is the correct interim state — fewer numbers on screen
 * beats an invented one.
 *
 * Restore from `GET /v1/admin/queues`, read once in the shell, never per badge
 * and never from a constant in this file.
 */

/** The sections the prototype opens on: the first seven. */
export const DEFAULT_OPEN_SECTIONS = 7

export interface Located {
  readonly section: NavSection
  readonly item: NavItem
}

export function findScreen(id: string): Located | undefined {
  for (const section of NAV) {
    for (const item of section[2]) {
      if (item[0] === id) return { section, item }
    }
  }
  return undefined
}

export function allScreenIds(): string[] {
  return NAV.flatMap((section) => section[2].map((item) => item[0]))
}

export function phaseOneIds(): string[] {
  return NAV.flatMap((section) => section[2].filter((item) => item[2] === 1).map((item) => item[0]))
}

/** Section label for a screen id, for the breadcrumb. */
export function sectionOf(id: string): string {
  return findScreen(id)?.section[0] ?? 'Dashboard'
}

/** Screen label for a screen id, for the breadcrumb. */
export function labelOf(id: string): string {
  return findScreen(id)?.item[1] ?? 'Today'
}

export function phaseOf(id: string): Phase {
  return findScreen(id)?.item[2] ?? 1
}
